import React, { createContext, useContext, useEffect, useRef, useState, useCallback } from 'react';
import { io } from 'socket.io-client';
import { notification, Avatar } from 'antd';
import { socketUrl } from '../config';

const SocketContext = createContext(null);

function initials(name) {
  if (!name) return '?';
  return name.trim().split(' ').map(w => w[0]).join('').toUpperCase().slice(0, 2);
}

export function SocketProvider({ children }) {
  const socketRef   = useRef(null);
  const [socket, setSocket]           = useState(null);
  const [connected, setConnected]     = useState(false);
  const [onlineUsers, setOnlineUsers] = useState(new Set());
  // Track which conversation is currently open (set by Chat.js)
  const activeConvIdRef = useRef(null);
  const setActiveConvId = useCallback((id) => { activeConvIdRef.current = id; }, []);

  useEffect(() => {
    const token = localStorage.getItem('accessToken');
    if (!token) return;

    const sock = io(socketUrl, {
      auth: { token },
      transports: ['websocket', 'polling'],
      reconnectionAttempts: 10,
      reconnectionDelay: 2000,
    });

    socketRef.current = sock;
    setSocket(sock);

    sock.on('connect', () => {
      setConnected(true);
      // Ask server for current online list
      sock.emit('get_online_users');
    });

    sock.on('disconnect', () => setConnected(false));

    sock.on('connect_error', (err) => {
      console.warn('[Socket] connect_error:', err.message);
    });

    // ── Online presence ──────────────────────────────────────────────────
    sock.on('online_users', ({ userIds }) => {
      setOnlineUsers(new Set(userIds.map(Number)));
    });

    sock.on('user_online', ({ userId }) => {
      setOnlineUsers(prev => new Set([...prev, Number(userId)]));
    });

    sock.on('user_offline', ({ userId }) => {
      setOnlineUsers(prev => {
        const next = new Set(prev);
        next.delete(Number(userId));
        return next;
      });
    });

    // ── Global new message → popup notification ──────────────────────────
    sock.on('new_message', (msg) => {
      // Don't popup if user is already viewing this conversation (coerce types)
      if (Number(activeConvIdRef.current) === Number(msg.conversation_id)) return;

      // Skip own messages — coerce both to number to avoid "5" !== 5 mismatch
      try {
        const payload = JSON.parse(atob(token.split('.')[1]));
        if (Number(payload.id) === Number(msg.sender_id)) return;
      } catch (_) {}

      // Determine if user is on the chat page right now
      const onChatPage = window.location.pathname.includes('/staff-chat');

      // Show notification popup
      const key = `msg-${msg.id}-${Date.now()}`;
      notification.open({
        key,
        message: (
          <span style={{ fontWeight: 700, color: '#7a0000', fontSize: 14 }}>
            {msg.sender_name || 'New Message'}
          </span>
        ),
        description: (
          <div>
            <div style={{ color: '#374151', fontSize: 13, marginBottom: 4, maxWidth: 260, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {msg.message}
            </div>
            {!onChatPage && (
              <span
                style={{ color: '#2563eb', fontSize: 12, cursor: 'pointer', fontWeight: 600 }}
                onClick={() => {
                  notification.close(key);
                  window.location.href = '/staff-chat';
                }}
              >
                Open Chat →
              </span>
            )}
          </div>
        ),
        icon: (
          <Avatar size={36} style={{ background: '#1890ff', color: '#fff', fontWeight: 700 }}>
            {initials(msg.sender_name)}
          </Avatar>
        ),
        placement: 'bottomRight',
        duration: 6,
        style: {
          borderLeft: '4px solid #1890ff',
          borderRadius: 10,
          boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
        },
      });
    });

    return () => {
      sock.disconnect();
      socketRef.current = null;
      setSocket(null);
    };
  }, []);

  return (
    <SocketContext.Provider value={{ socket, connected, onlineUsers, setActiveConvId }}>
      {children}
    </SocketContext.Provider>
  );
}

export function useSocket() {
  return useContext(SocketContext);
}

export default SocketContext;
